/**
 * .tuMetrics {"tu1": 0.9, "tu2": 0.93, "tu3": 3, "notes": "Session profile + membership → User", "tuVersion": "2.5"}
 * .help "Builds the signed-in person: email, company, guest flag, invite accepted. Used by login and token refresh."
 */
import type { AccessRole, ActivityRole, User } from '../../types/index.ts';

/** CamelCase row from initializeUser (public.users + membership roles). */
export type InitializedProfile = {
  id: string;
  email?: string | null;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  avatar?: string | null;
  accessRole?: AccessRole | string;
  activityRoles?: ActivityRole[];
  reportsTeamId?: string | null;
  inFlowState?: boolean;
  team?: string | null;
  companyId?: string | null;
  enableBackgroundAnimation?: boolean;
  animationIntensity?: number;
  backgroundType?: string | null;
  headlineFeedBundle?: string | null;
  headlineFeedKeyword?: string | null;
  warpSpeed?: number;
  backplanePinEnabled?: boolean;
  backplaneDeskCleared?: boolean;
  isAgent?: boolean;
  agentType?: string | null;
  llmModel?: string | null;
  agentInstructions?: string | null;
  agentCurrentPeriodTokensUsed?: number | null;
  agentTokenLimitPerPeriod?: number | null;
  agentLastTokenResetDate?: string | null;
  agentLastTokenWarningSent?: string | null;
  agentUserObservations?: Record<string, unknown> | null;
  agentCapabilities?: Record<string, boolean> | null;
  casualName?: string | null;
  bio?: string | null;
  skills?: string[] | null;
  funFact?: string | null;
  pronouns?: string | null;
  timezone?: string | null;
  workCity?: string | null;
  workLat?: number | null;
  workLng?: number | null;
};

export type MembershipSnapshot = {
  company_id?: string | null;
  invite_accepted?: boolean | null;
  is_guest?: boolean | null;
  guest_access_level?: unknown;
  deactivated_at?: string | null;
  companies?: unknown;
};

export function parseGuestAccess(value: unknown): 'team' | 'company' | null {
  return value === 'team' || value === 'company' ? value : null;
}

export function companyDomainFromMembership(membership: MembershipSnapshot | null | undefined): string | null {
  const companies = membership?.companies;
  if (!companies) return null;
  const row = Array.isArray(companies) ? companies[0] : companies;
  if (typeof row !== 'object' || row === null) return null;
  const domain = (row as { domain?: unknown }).domain;
  return typeof domain === 'string' ? domain : null;
}

export function composeCurrentUser(input: {
  raw: InitializedProfile;
  sessionEmail?: string | null;
  membership: MembershipSnapshot | null | undefined;
}): User {
  const { raw, sessionEmail, membership } = input;
  const companyFromMembership = membership?.company_id ?? null;
  return {
    id: raw.id,
    email: raw.email ?? sessionEmail ?? '',
    name: raw.name || sessionEmail?.split('@')[0] || 'Anonymous',
    firstName: raw.firstName,
    lastName: raw.lastName,
    avatar: raw.avatar || '',
    accessRole: (raw.accessRole || 'member') as AccessRole,
    activityRoles: (raw.activityRoles || []) as ActivityRole[],
    reportsTeamId: raw.reportsTeamId ?? null,
    inFlowState: raw.inFlowState ?? false,
    team: raw.team ?? null,
    companyId: raw.companyId ?? companyFromMembership,
    enableBackgroundAnimation: raw.enableBackgroundAnimation || false,
    animationIntensity: raw.animationIntensity || 0.2,
    backgroundType: raw.backgroundType || 'network',
    warpSpeed: raw.warpSpeed || 5,
    headlineFeedBundle: raw.headlineFeedBundle || 'mixed',
    headlineFeedKeyword: raw.headlineFeedKeyword || null,
    backplanePinEnabled: !!raw.backplanePinEnabled,
    backplaneDeskCleared: !!raw.backplaneDeskCleared,
    isAgent: raw.isAgent || false,
    agentType: raw.agentType || undefined,
    llmModel: raw.llmModel || undefined,
    agentInstructions: raw.agentInstructions || undefined,
    agentCurrentPeriodTokensUsed: raw.agentCurrentPeriodTokensUsed || 0,
    agentTokenLimitPerPeriod: raw.agentTokenLimitPerPeriod || 100000,
    agentLastTokenResetDate: raw.agentLastTokenResetDate || undefined,
    agentLastTokenWarningSent: raw.agentLastTokenWarningSent || undefined,
    agentUserObservations: raw.agentUserObservations || undefined,
    agentCapabilities: raw.agentCapabilities || undefined,
    casualName: raw.casualName || undefined,
    bio: raw.bio || undefined,
    skills: raw.skills || undefined,
    funFact: raw.funFact || undefined,
    pronouns: raw.pronouns || undefined,
    timezone: raw.timezone || null,
    workCity: raw.workCity || null,
    workLat: typeof raw.workLat === 'number' ? raw.workLat : null,
    workLng: typeof raw.workLng === 'number' ? raw.workLng : null,
    inviteAccepted: !!membership?.invite_accepted,
    isGuest: !!membership?.is_guest,
    guestAccessLevel: parseGuestAccess(membership?.guest_access_level),
    companyDomain: companyDomainFromMembership(membership),
  };
}
